import React, { useMemo, useState, useCallback } from "react"

export type SmartImageProps = {
  src: string
  alt: string
  width?: number | string
  height?: number | string
  className?: string
  style?: React.CSSProperties
  // Performance
  lazy?: boolean // maps to loading="lazy" vs "eager"
  priority?: boolean // maps to fetchpriority="high"
  decoding?: "async" | "sync" | "auto"
  sizes?: string
  srcSet?: string
  // <picture> sources for modern formats (e.g., WebP/AVIF)
  sources?: Array<{ srcSet: string; type?: string; media?: string }>
  // Layout
  aspectRatio?: string | number // e.g. "16/9" or 1.777...
  objectFit?: React.CSSProperties["objectFit"]
  autoAspectRatio?: boolean // compute from naturalWidth:naturalHeight on load
  // Fallbacks
  fallbackSrc?: string // static fallback image
  fallbackRender?: (args: { alt: string }) => React.ReactNode
  showAltOnError?: boolean // additionally show alt text when error occurs
  // Events
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void
  // SSR
  prefetched?: boolean // when true, treat as already loaded (SSR hydration)
}

/**
 * Framework-agnostic core: status state machine for image loading
 */
export type ImageStatus = "idle" | "loading" | "loaded" | "error"

export function nextStatus(event: "load" | "error"): ImageStatus {
  if (event === "load") return "loaded"
  return "error"
}

/**
 * SmartImage: React wrapper that renders a responsive image with spinner and fallbacks.
 * - Shows spinner while loading
 * - On error, logs and shows fallback (image or custom UI or alt text)
 * - Supports aspect ratio enforcement via CSS aspect-ratio
 */
export const SmartImage = React.memo(function SmartImage(props: SmartImageProps) {
  const {
    src,
    alt,
    width,
    height,
    className,
    style,
    lazy = true,
    priority = false,
    decoding = "async",
    sizes,
    srcSet,
    sources,
    aspectRatio,
    objectFit = "cover",
    autoAspectRatio = false,
    fallbackSrc = "/images/placeholder.svg",
    fallbackRender,
    showAltOnError = true,
    onLoad,
    onError,
    prefetched = false,
  } = props

  const [status, setStatus] = useState<ImageStatus>(prefetched ? "loaded" : "loading")
  const [currentSrc, setCurrentSrc] = useState<string>(src)
  const [computedAspect, setComputedAspect] = useState<string | undefined>(undefined)

  const wrapperStyle = useMemo<React.CSSProperties>(() => {
    const s: React.CSSProperties = {
      position: "relative",
      ...style,
    }
    const ar = computedAspect || (aspectRatio ? (typeof aspectRatio === "number" ? String(aspectRatio) : aspectRatio) : undefined)
    if (ar) s.aspectRatio = ar
    if (width !== undefined) s.width = width
    if (height !== undefined) s.height = height
    return s
  }, [style, aspectRatio, width, height, computedAspect])

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setStatus(() => nextStatus("load"))
      // Auto compute aspect ratio from natural size to avoid cropping/distortion
      if (autoAspectRatio) {
        const imgEl = e.currentTarget
        const nw = imgEl.naturalWidth
        const nh = imgEl.naturalHeight
        if (nw > 0 && nh > 0) {
          setComputedAspect(`${nw}/${nh}`)
        }
      }
      onLoad?.(e)
    },
    [onLoad, autoAspectRatio]
  )

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      // Log and swap to fallback source if not already using it
      const img = e.currentTarget
      if (img.src !== window.location.origin + fallbackSrc && currentSrc !== fallbackSrc) {
        setCurrentSrc(fallbackSrc)
        // Set to loading so the image element can try to load the fallback
        setStatus("loading")
      } else {
        // Fallback also failed
        setStatus("error")
      }
      onError?.(e)
    },
    [fallbackSrc, onError, currentSrc]
  )

  const showSpinner = status === "loading"
  const showError = status === "error" && !fallbackRender
  const showImg = status !== "error"

  return (
    <div className={className} style={wrapperStyle} aria-busy={showSpinner} aria-live="polite">
      {/* Image element (optionally wrapped in <picture> for modern formats) */}
      {showImg && (Array.isArray(sources) && sources.length > 0 ? (
        <picture>
          {sources.map((s, idx) => (
            <source key={idx} srcSet={s.srcSet} type={s.type} media={s.media} />
          ))}
          <img
            src={currentSrc}
            alt={alt}
            loading={priority ? "eager" : lazy ? "lazy" : "eager"}
            decoding={decoding}
            sizes={sizes}
            srcSet={srcSet}
            style={{ width: "100%", height: "100%", objectFit }}
            // fetch priority removed to avoid React unknown prop warning; rely on loading="eager" when priority
            onLoad={handleLoad}
            onError={handleError}
          />
        </picture>
      ) : (
        <img
          src={currentSrc}
          alt={alt}
          loading={priority ? "eager" : lazy ? "lazy" : "eager"}
          decoding={decoding}
          sizes={sizes}
          srcSet={srcSet}
          style={{ width: "100%", height: "100%", objectFit }}
          // fetch priority removed to avoid React unknown prop warning; rely on loading="eager" when priority
          onLoad={handleLoad}
          onError={handleError}
        />
      ))}

      {/* Spinner overlay while loading */}
      {showSpinner && (
        <div
          role="status"
          aria-label="Loading image"
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(90deg, rgba(243,244,246,0.9) 25%, rgba(229,231,235,0.9) 37%, rgba(243,244,246,0.9) 63%)",
            backgroundSize: "400% 100%",
            animation: "smartimage-shimmer 1.1s ease-in-out infinite",
          }}
        >
          <style>
            {`@keyframes smartimage-shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }`}
          </style>
        </div>
      )}

      {/* Custom fallback renderer */}
      {status === "error" && fallbackRender && (
        <div
          style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {fallbackRender({ alt })}
        </div>
      )}

      {/* Alt text overlay when images disabled or error */}
      {showError && showAltOnError && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#1f2937",
            fontWeight: 500,
            background: "#f3f4f6",
          }}
        >
          {alt}
        </div>
      )}
    </div>
  )
})

export default SmartImage
